import BaseModel from "lib/model";
import MediaGalleryCollection from "collections/galleries";
import ViewCollection from "collections/views";
import CompanyCollection from "collections/companies";
import SchemaCollection from "collections/schemas";
import SettingsModel from "./brand/settings";
import ModulesModel from "./brand/modules";
import ContactInformationModel from "./brand/contact_information";
import moment from "moment";
import me from "models/me";
import merge from "merge";
import Cart from "./cart";
import Immutable from "immutable";
import siteConfig from "site-config";

// This module will get expanded to all brand data when redoing gallery
export default BaseModel.extend({
    props: {
        _id: "string",
        merchant_id: "string",

        type: "string",
        can_export_to_ftp: {
            type: "boolean",
            required: true,
            default: false,
        },
        active: "boolean",
        name: "string",
        available_modules: {
            type: "array",
            required: true,
            default: function () {
                return [];
            },
        },
        site_version: "number",
        disabled: {
            type: "boolean",
            required: true,
            default: false,
        },
        default_permissions: { type: "object" },
        __virtualConnection: "boolean",
    },
    session: {
        has_profile: {
            type: "boolean",
        },
    },
    collections: {
        galleries: MediaGalleryCollection,
        companies: CompanyCollection,
        schemas: SchemaCollection,
        views: ViewCollection,
    },

    children: {
        settings: SettingsModel,
        modules: ModulesModel,
        contact_information: ContactInformationModel,
        activeCart: Cart,
    },

    derived: {
        _initials: {
            deps: ["name"],
            cache: false,
            fn() {
                let initals = "";
                if (this.name) {
                    const chunks = this.name.split(" ");
                    for (let i = 0; i < chunks.length; i++) {
                        if (i >= 2) {
                            break;
                        }
                        initals = initals + (chunks[i][0] || "");
                    }
                }

                return initals.toUpperCase();
            },
        },
        _hasActiveGalleries: {
            deps: ["galleries"],
            fn() {
                return !!this.galleries.getActive().length;
            },
        },

        _users: {
            deps: ["modules"],
            fn() {
                return this.modules.base.user_connections;
            },
        },

        _brandConnection: {
            deps: ["modules"],
            fn() {
                let brandUser = {};
                if (me.account_type === "brand") {
                    brandUser = this.modules.base.user_connections.get(me._id);
                } else if (me.account_type === "nuorder") {
                    return {
                        //stub for nuorder users
                        // TODO: revert the USD we hacked in SAKS
                        currency_code: "USD", //this.settings.default_currency || this.settings.currencies[0],
                    };
                }

                return brandUser;
            },
        },

        _traits: {
            deps: ["active"],
            cached: true,
            fn() {
                let status = this.active ? "Paying" : "Cancelled";
                if (me.account_type === "nuorder") {
                    status = "Cancelled";
                }
                const traits = {
                    Status: status,
                    "Create Date": moment.utc(this.created_on).format("YYYY-MM-DD"),
                    Licenses: this.modules.base.seats,
                    Seats: this.modules.base.seats,
                    name: this.name,
                    "Default Currency": this.settings.default_currency,
                    Currencies: (this.settings.currencies || []).join(","),
                    "Enabled Modules": (this.available_modules || []).join(","),
                    "Subteam Count": this.settings.subteams.length,
                    Subscription: this.settings.subscription_type,
                };

                (this.settings.preferences || []).forEach(function (p) {
                    traits["zpref_" + p.preference] = p.value;
                });
                return traits;
            },
        },
    },

    urlRoot: "/brand",

    hasColorSwatch() {
        return !!this.modules.base.color_swatches.length;
    },

    getSafeV3URI(pathname) {
        const fragments = pathname.split("/").filter((fragment) => fragment !== "");

        if (fragments.length !== 3 || !["admin", "management", "reporting", "orders"].includes(fragments[2])) {
            pathname = `/brand/${this._id}`;
        }
        return pathname;
    },

    getColorSwatch() {
        const swatch = this.modules.base.color_swatches.findWhere({
            color: this.color,
        });
        if (swatch) {
            switch (swatch.swatch_type) {
                case "hex":
                    return `background: #${swatch.value}`;
                case "file":
                    return (
                        "background:url(//cdn2.nuorder.com/brand_color_swatch/" +
                        swatch.value +
                        ".jpg) no-repeat center center; background-size: 100% 100%;"
                    );
                default:
                    return `background:url(${siteConfig.assetPath}/images/noColorSwatch_v2_60x60.png) no-repeat center center; background-size: 100% 100%;`;
            }
        }

        return `background:url(${siteConfig.assetPath}/images/noColorSwatch_v2_60x60.png) no-repeat center center; background-size: 100% 100%;`;
    },

    hasPreference(preference) {
        if (this.settings._prefs) {
            return this.settings._prefs[preference] || false;
        } else {
            return false;
        }
    },

    buyersCanOrder() {
        const buyerPermissions = this.default_permissions.buyer;
        return buyerPermissions && buyerPermissions.indexOf("can_order") !== -1;
    },

    userHasPermission(user, permission) {
        let userConnection = { role: "buyer" };
        if (this.account_type === "nuorder") {
            return true;
        } else if (user.account_type === "brand") {
            userConnection = this.findUserConnection(user);
            if (!userConnection) {
                return false;
            }

            // See if they have an override
            if (userConnection.permissions.length) {
                return userConnection.permissions.indexOf(permission) !== -1;
            }
        }

        let permissionsForRole = this.default_permissions[userConnection.role];
        const brandAllowsGuestBuyer = this.hasPreference("allow_guest_buyers");
        const isGuestBuyer = userConnection.role === "buyer" && !user.activeCompany._id;
        if (brandAllowsGuestBuyer && isGuestBuyer) {
            //have buyers use the guest buyer permission set when viewing a brand they are not connected to
            permissionsForRole = this.default_permissions["guest buyer"] || [];
        }

        return permissionsForRole.includes(permission);
    },

    findUserConnection(user) {
        const userId = user._id || user;
        return this.modules.base.user_connections.findWhere({ ref: userId });
    },

    searchBrandUsers(string) {
        return this.modules.base.user_connections.filter((brandUser) => {
            const pattern = new RegExp(string);
            return brandUser.email.match(pattern, "i") || brandUser.name.match(pattern, "i");
        });
    },

    trackGroup() {
        if (me.isSignedIn()) {
            const groupId = this._id;
            const { _traits } = this;
            _traits.product_id = me.account_type;
            _traits.parent_id = `${this._id}_s`;
            global.analytics.group(groupId, _traits);
        }
    },

    trackAction(action, data = {}) {
        if (me.isSignedIn()) {
            data = merge(true, data);
            data.category = data.category || "Wholesale";

            const groupId = this._id;
            data.GroupID = groupId;
            global.analytics.track(action, data, { groupId });
        }
    },

    pushItemViewed(item) {
        this._recentlyViewedItems = this._recentlyViewedItems || Immutable.List();
        this._recentlyViewedItems = this._recentlyViewedItems.filter((recentItem) => {
            return recentItem !== item;
        });
        this._recentlyViewedItems = this._recentlyViewedItems.unshift(item);
        if (this._recentlyViewedItems.size > 10) {
            this._recentlyViewedItems = this._recentlyViewedItems.pop();
        }
        return this._recentlyViewedItems;
    },

    getRecentlyViewedItems() {
        this._recentlyViewedItems = this._recentlyViewedItems || Immutable.List();
        return this._recentlyViewedItems;
    },
    getBrandUserSubteam(teamName) {
        if (teamName) {
            return this.settings.subteams.get(teamName);
        } else {
            return this.settings.subteams.at(0);
        }
    },
    getWarehouses() {
        return this.settings.warehouses;
    },
    getRoles() {
        return Object.keys(this.settings.roles);
    },
    canCreateBrandProfile() {
        return this.hasPreference("allow_guest_buyers") && this.hasPreference("hide_from_buyers") === false;
    },
    hasModuleEnabled(module) {
        if (this.modules[module]) {
            return this.modules[module].enabled;
        } else {
            return false;
        }
    },
});
